<template>
  <div class="cart-view" style="position: relative;">
    <v-icon v-if="dialog"
            @click="closeDialog"
            style="position: absolute; top: 0; right: 0; cursor: pointer;">
      fas fa-xmark
    </v-icon>
    <div v-if="loading" class="d-flex justify-center pa-8">
      <v-progress-circular indeterminate color="primary" />
    </div>

    <div v-else>
      <!-- Sélecteur établissement -->
      <div v-if="!dialog && carts.length > 1" class="d-flex align-center mb-5">
        <span class="etablissement-label me-4">Panier pour mon établissement</span>
        <v-select v-model="selectedOwnerIdx"
                  :items="cartSelectItems"
                  density="compact"
                  variant="outlined"
                  hide-details
                  style="max-width: 260px" />
      </div>

      <!-- Filtres -->
      <div v-if="hasPaieItems" class="cart-filters mb-4">
        <v-select v-model="selectedProjet"
                  :items="projetSelectItems"
                  label="Par projet"
                  density="compact"
                  variant="outlined"
                  hide-details />
        <v-select v-model="selectedEcheance"
                  :items="echeanceSelectItems"
                  label="Échéance"
                  density="compact"
                  variant="outlined"
                  hide-details />
      </div>

      <!-- Items -->
      <v-card v-for="(item, itemIndex) in filteredItems"
              :key="`${item.objType}-${item.objId}`"
              class="cart-item mb-2"
              flat
              rounded="lg"
              :ripple="false"
              @click="!isRequired(item) && !isInformational(item) && (item.selected = !item.selected)">
        <div class="d-flex align-center pa-4">
          <v-checkbox v-model="item.selected"
                      :disabled="isRequired(item) || isInformational(item)"
                      density="compact"
                      hide-details
                      class="me-2 flex-shrink-0" />
          <div class="flex-grow-1">
            <div class="d-flex align-center">
              <div class="item-label flex-grow-1">{{ getItemLabel(item) }}</div>
              <span class="item-amount">
                {{ formatAmount(item.selectedTtc) }}
                <span v-if="item.label.endsWith(' — rejet SDD')">rejet SDD</span>
              </span>
            </div>
            <div v-if="hasPayrollBalanceDelta(item)" class="item-cost mt-1">
              Coût : {{ formatAmount(getPayrollCost(item) ?? 0) }},
              déjà réglé : {{ formatAmount(getPayrollPaidAmount(item)) }}
            </div>
            <v-chip v-if="item.projet"
                    class="item-projet mt-2"
                    size="small"
                    variant="tonal"
                    :ripple="false"
                    @click.stop>
              {{ item.projet }}
            </v-chip>
            <div v-if="getDetails(item).length > 0" class="cart-item-details mt-1 ms-2">
              <div v-for="(detail, detailIndex) in getDetails(item)"
                   :key="`${itemIndex}-${detail.objId}-${detailIndex}`"
                   class="cart-item-detail d-flex align-center"
                   data-testid="payroll-adjustment-detail">
                <div class="item-label flex-grow-1">{{ detail.label }}</div>
                <span class="item-amount">{{ formatAmount(detail.selectedTtc) }}</span>
              </div>
            </div>
          </div>
        </div>
      </v-card>

      <!-- Footer -->
      <div class="cart-footer mt-6">
        <p class="total-label mb-5">
          Total : <strong>{{ formatAmount(total) }}</strong>
        </p>
        <div v-if="dialog" class="d-flex flex-row-reverse">
          <loader-button v-if="hasPayableSelected || showRepayButton"
                         :label="payLabel"
                         :loading="saving"
                         @callback="handlePay" />
        </div>
        <v-btn v-else-if="hasPayableSelected || showRepayButton"
               class="base"
               block
               size="x-large"
               variant="flat"
               rounded="lg"
               :loading="saving"
               @click="handlePay">
          {{ payLabel }}
        </v-btn>
      </div>
    </div>
  </div>
</template>

<script lang="ts">
  import { defineComponent } from 'vue';
  import Cart, { CartItem } from '@/extend/classes/Cart';
  import Contrat from '@/generated/classes/Contrat';
  import Paie, { PaieStatuts } from '@/generated/classes/Paie';
  import { getCurrency } from '@/framework/base/helpers/Localization';
  import { ListFilter } from '@/extend/classes/ListData';
  import { ColumnFilter, ListChoix } from '@/generated/classes/ListData';
  import { ObjTypes } from '@/generated/classes/ObjTypes';
  import { PaymentMeansTypes } from '@/generated/classes/PaymentMeans';
  import { Sections } from '@/generated/classes/AppSchema';
  import LoaderButton from '@/framework/base/components/LoaderButton.vue';
  import Route from '@/generated/classes/Route';
  import { CiRouter } from '@/framework/base/helpers/NavHelper';
  import HubServices from '@/generated/hub/HubServices';
  import { storeContrat, storePaie, storeSalarie, storeSession } from '@/generated/stores';
  import UserProfile, { EnumProfile } from '@/framework/base/classes/UserProfile';

  export default defineComponent({
    name: 'SectionCartItemList',

    components: { LoaderButton },

    props: {
      hideTitle: { type: Boolean },
      isAbstract: { type: Boolean },
      dialog: { type: Boolean, default: false },
      etablissementId: { type: Number, default: 0 },
    },

    data() {
      return {
        sectionName: Sections.CART_ITEM_LIST,
        carts: [] as Cart[],
        selectedOwnerIdx: 0,
        selectedProjet: null as string | null,
        selectedEcheance: null as string | null,
        saving: false,
        loading: true,
        targetEtablissementId: 0,
      };
    },

    created() {
      if (this.etablissementId > 0) {
        this.targetEtablissementId = this.etablissementId;
      } else {
        const routeId = CiRouter.getRoute()?.sectionObjId ?? 0;
        if (routeId > 0) this.targetEtablissementId = routeId;
      }
      HubServices.onCartUpdated(this.updateCart);
      Cart.list(this.cartListFilter).then((data) => {
        this.carts = (data.reccords as any[]).map((c: any) => Cart.fromJson(c));
        this.applyEtablissementSelection();
        this.initSelection();
        this.loading = false;
      }).catch(() => {
        this.loading = false;
      });
    },

    computed: {
      selectedCart(): Cart | null {
        return this.carts[this.selectedOwnerIdx] ?? null;
      },

      cartItems(): CartItem[] {
        return this.selectedCart?.cartItems ?? [];
      },

      projets(): string[] {
        const seen = new Set<string>();
        return this.cartItems
          .map(i => i.projet)
          .filter((p): p is string => !!p && !seen.has(p) && !!seen.add(p));
      },

      hasPaieItems(): boolean {
        return this.cartItems.some(i => i.objType === ObjTypes.PAIE);
      },

      paieFilterSignature(): string {
        return this.cartItems
          .filter(item => item.objType === ObjTypes.PAIE)
          .map(item => `${item.objId}:${this.getPaie(item)?.statut ?? PaieStatuts.NONE}`)
          .join('|');
      },

      projetSelectItems(): { title: string; value: string | null }[] {
        return [
          { title: 'Tous les projets', value: null },
          ...this.projets.map(projet => ({ title: projet, value: projet })),
        ];
      },

      echeanceSelectItems(): { title: string; value: string | null }[] {
        return [
          { title: 'Toutes les échéances', value: null },
          { title: 'Règlements anticipés', value: 'ANTICIPE' },
          { title: 'Règlements après validation', value: 'APRES_VALIDATION' },
        ];
      },

      filteredItems(): CartItem[] {
        const items = this.cartItems.filter(item => !this.isDetail(item));
        if (!this.selectedProjet && !this.selectedEcheance) {
          return items;
        }
        return items.filter(item => this.isAlwaysVisibleWithFilters(item) || this.matchesFilters(item));
      },

      selectedItems(): CartItem[] {
        return this.cartItems.filter(i => i.selected && !this.isDetail(i) && !this.isInformational(i));
      },

      hasPayableSelected(): boolean {
        return this.selectedItems.some(i => i.selectedTtc > 0 && !i.isMinimumPaymentCarry);
      },

      canUseMinimumPaymentCredit(): boolean {
        const minimum = this.selectedCart?.minimumPaymentAmount ?? 0;
        return this.hasPayableSelected && minimum > 0 && this.total > 0 && this.total < minimum;
      },

      isAllAvoir(): boolean {
        const payableItems = this.cartItems.filter(i => !this.isDetail(i) && !this.isInformational(i));
        const avoirMontant = payableItems
          .filter(i => i.objType === ObjTypes.REFUNDABLE && i.selectedTtc < 0)
          .reduce((s, i) => s - i.selectedTtc, 0);
        if (avoirMontant <= 0) return false;

        const fraisMontant = payableItems
          .filter(i => i.objType === ObjTypes.FRAIS && i.selectedTtc > 0)
          .reduce((s, i) => s + i.selectedTtc, 0);

        const onlyAvoirRepayOrFrais = payableItems.every(i =>
          ((i.objType === ObjTypes.REFUNDABLE || i.objType === ObjTypes.REUSABLE) && i.selectedTtc < 0) ||
          i.objType === ObjTypes.REPAY ||
          (i.objType === ObjTypes.FRAIS && i.selectedTtc > 0)
        );

        const montantRemboursable = avoirMontant - fraisMontant;
        return onlyAvoirRepayOrFrais &&
          montantRemboursable > 0 &&
          montantRemboursable >= (this.selectedCart?.minRepay ?? 0);
      },

      isAdmin(): boolean {
        const user = storeSession.connectedUser;
        if (!user) return false;
        return UserProfile.hasProfile(user, EnumProfile.admin);
      },

      showRepayButton(): boolean {
        if (this.isAdmin) {
          return this.cartItems.some(i => i.objType === ObjTypes.REPAY);
        }
        return this.isAllAvoir;
      },

      payLabel(): string {
        if (this.showRepayButton) {
          return this.isAdmin ? 'Rembourser' : 'Demander remboursement';
        }
        return this.canUseMinimumPaymentCredit ? 'Utiliser le crédit autorisé' : 'Régler';
      },

      total(): number {
        return this.selectedItems.reduce((sum, i) => sum + i.selectedTtc, 0);
      },

      allReadOnly(): boolean {
        return false;
      },

      cartSelectItems(): { title: string; value: number }[] {
        return this.carts.map((c, idx) => ({ title: c.ownerLabel, value: idx }));
      },

      cartListFilter(): ListFilter {
        const filter = new ListFilter();
        if (this.targetEtablissementId > 0) {
          const cf = new ColumnFilter();
          cf.name = 'ownerId';
          cf.staticFilter = true;
          const choix = new ListChoix();
          choix.value = this.targetEtablissementId;
          cf.filters.push(choix);
          filter.columnsFilter.push(cf);
        }
        return filter;
      },
    },

    watch: {
      '$route.query.etablissementId': {
        immediate: true,
        handler(newVal: string) {
          const id = parseInt(newVal ?? '0') || 0;
          if (id > 0) {
            this.targetEtablissementId = id;
            if (this.carts.length > 0) {
              this.applyEtablissementSelection();
            }
          }
        },
      },
      selectedOwnerIdx() {
        this.selectedProjet = null;
        this.selectedEcheance = null;
        this.initSelection();
      },
      selectedProjet() {
        this.applyFilterSelection();
      },
      selectedEcheance() {
        this.applyFilterSelection();
      },
      paieFilterSignature() {
        if (this.selectedProjet || this.selectedEcheance) {
          this.applyFilterSelection();
        }
      },
    },

    methods: {
      setFocus(route: Route) {
        this.targetEtablissementId = route?.sectionObjId ?? 0;
        if (this.carts.length > 0) {
          this.applyEtablissementSelection();
        }
      },

      applyEtablissementSelection() {
        if (this.targetEtablissementId > 0) {
          const idx = this.carts.findIndex(c => c.ownerId === this.targetEtablissementId);
          if (idx >= 0) this.selectedOwnerIdx = idx;
        }
      },

      isRequired(item: CartItem): boolean {
        return item.isMinimumPaymentCarry ||
          item.objType === ObjTypes.FRAIS ||
          (item.objType === ObjTypes.SUBSCRIPTION && item.selectedTtc > 0) ||
          (item.objType === ObjTypes.REFUNDABLE && item.selectedTtc > 0);
      },

      isInformational(item: CartItem): boolean {
        return item.objType === ObjTypes.AUTHORIZED_CREDIT;
      },

      isDetail(item: CartItem): boolean {
        return item.objType === ObjTypes.ACCOUNT_ENTRY;
      },

      getDetails(item: CartItem): CartItem[] {
        if (item.objType === ObjTypes.REFUNDABLE && item.selectedTtc < 0) {
          return this.cartItems.filter(detail => this.isDetail(detail));
        }
        return [];
      },

      initSelection() {
        this.cartItems.forEach(item => {
          item.selected = !this.isDetail(item) && !this.isInformational(item);
        });
      },

      getPaie(item: CartItem): Paie | null {
        if (item.objType !== ObjTypes.PAIE || item.objId <= 0) return null;
        return storePaie.getPaie(item.objId);
      },

      getContrat(item: CartItem): Contrat | null {
        const paie = this.getPaie(item);
        if (!paie?.contratId) return null;
        return storeContrat.getContrat(paie.contratId);
      },

      isPaieLoaded(paie: Paie | null): paie is Paie {
        return !!paie?.contratId;
      },

      isContratLoaded(contrat: Contrat | null): contrat is Contrat {
        return !!contrat?.codeCheque && !!contrat.salarieId && !!contrat.emploiNom;
      },

      isReglementAnticipe(item: CartItem): boolean {
        const paie = this.getPaie(item);
        if (this.isPaieLoaded(paie)) {
          return paie.statut === PaieStatuts.PRE_IN_CART ||
            paie.statut === PaieStatuts.PRE_PAYED ||
            paie.statut === PaieStatuts.PRE_COUNTED ||
            paie.statut === PaieStatuts.WAITING_END;
        }
        return new Date(item.date).getTime() > Date.now();
      },

      getPayrollCost(item: CartItem): number | null {
        const paie = this.getPaie(item);
        if (item.objType !== ObjTypes.PAIE || !this.isPaieLoaded(paie)) return null;
        const cost = Number(paie.synthesePaie?.total);
        return Number.isFinite(cost) ? cost : null;
      },

      hasPayrollBalanceDelta(item: CartItem): boolean {
        const cost = this.getPayrollCost(item);
        return cost !== null && cost - item.selectedTtc >= 0.01;
      },

      getPayrollPaidAmount(item: CartItem): number {
        const cost = this.getPayrollCost(item);
        return cost === null ? 0 : Math.max(0, cost - item.selectedTtc);
      },

      getItemLabel(item: CartItem): string {
        const paie = this.getPaie(item);
        const contrat = this.getContrat(item);
        if (!this.isPaieLoaded(paie) || !this.isContratLoaded(contrat)) return item.label;

        const salarie = storeSalarie.getSalarie(contrat.salarieId);
        if (!salarie?.prenom || !salarie?.nom || !contrat.dateFin) return item.label;

        const referencePaie = this.isReglementAnticipe(item) ? 'Régl. anticipé' : `paie ${paie.codeContrat}`;
        const prefix = this.hasPayrollBalanceDelta(item) ? 'Solde contrat' : 'Contrat';
        return `${prefix} ${contrat.codeCheque} / ${referencePaie} pour ${salarie.prenom} ${salarie.nom} ` +
          `(${contrat.emploiNom}) du ${this.formatDate(contrat.dateDebut)} au ${this.formatDate(contrat.dateFin)}`;
      },

      formatDate(value: Date): string {
        return new Date(value).toLocaleDateString('fr-FR');
      },

      getEcheance(item: CartItem): string {
        return this.isReglementAnticipe(item) ? 'ANTICIPE' : 'APRES_VALIDATION';
      },

      isAlwaysVisibleWithFilters(item: CartItem): boolean {
        return item.objType !== ObjTypes.PAIE;
      },

      matchesFilters(item: CartItem): boolean {
        if (item.objType !== ObjTypes.PAIE) return false;
        return (!this.selectedProjet || item.projet === this.selectedProjet) &&
          (!this.selectedEcheance || this.getEcheance(item) === this.selectedEcheance);
      },

      applyFilterSelection() {
        if (!this.selectedProjet && !this.selectedEcheance) {
          this.initSelection();
          return;
        }
        this.cartItems.forEach(item => {
          item.selected = !this.isDetail(item) && !this.isInformational(item) &&
            (this.isAlwaysVisibleWithFilters(item) || this.matchesFilters(item));
        });
      },

      updateCart() {
        this.saving = false;
        this.loading = true;
        Cart.list(this.cartListFilter).then((data) => {
          this.carts = (data.reccords as any[]).map((c: any) => Cart.fromJson(c));
          this.applyEtablissementSelection();
          this.initSelection();
          this.applyFilterSelection();
          this.loading = false;
        }).catch(() => { this.loading = false; });
      },

      formatAmount(value: number): string {
        return value ? getCurrency(value) : '0,00 €';
      },

      handlePay() {
        if (this.showRepayButton) {
          this.repay();
        } else {
          this.payer();
        }
      },

      repay() {
        if (!this.selectedCart) return;
        this.saving = true;
        const cart = this.selectedCart;
        cart.selectedTtc = this.total;
        Cart.repay(cart).then(() => {
          this.updateCart();
          this.saving = false;
        }).catch(() => {
          this.saving = false;
        });
      },

      closeDialog() {
        this.$emit('closeDialog');
      },

      payer() {
        if (!this.selectedCart || this.selectedItems.length === 0) return;
        this.saving = true;
        const cart = this.selectedCart;
        cart.selectedTtc = this.total;
        if (cart.paymentMeansType === PaymentMeansTypes.CARTE_BANCAIRE) {
          Cart.payWithPaybox(cart).then(() => {
            this.saving = false;
          }).catch(() => {
            this.saving = false;
          });
        } else {
          Cart.command(cart).then(() => {
            this.updateCart();
          }).catch(() => {
            this.saving = false;
          });
        }
      },
    },
  });
</script>

<style scoped>
  .cart-view {
    width: 100%;
  }

  .etablissement-label {
    font-size: 16px;
    font-weight: 500;
    color: #171A1C;
    white-space: nowrap;
  }

  .cart-item {
    border: 1px solid #F1F3F4 !important;
  }

  .cart-item-detail {
    background-color: transparent;
  }

  .cart-item-detail .item-label,
  .cart-item-detail .item-amount {
    color: #657074;
    font-size: 13px;
    font-weight: 400;
  }

  .cart-filters {
    display: grid;
    grid-template-columns: repeat(2, minmax(220px, 320px));
    gap: 16px;
  }

  .item-label {
    font-size: 14px;
    font-weight: 500;
    color: #171A1C;
  }

  .item-projet {
    font-size: 12px;
    font-weight: 400;
    color: #25A3A7;
  }

  .item-cost {
    color: #657074;
    font-size: 13px;
    font-weight: 400;
  }

  .item-amount {
    font-size: 14px;
    font-weight: 500;
    color: #171A1C;
    white-space: nowrap;
    margin-left: 16px;
  }

  .total-label {
    font-size: 20px;
    font-weight: 500;
    color: #171A1C;
  }

  @media (max-width: 700px) {
    .cart-filters {
      grid-template-columns: 1fr;
    }
  }
</style>
